import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Button, CampaignIcon, Card, RuleAudienceIcon } from '@/lib';
import { RouteConstants } from '@/features/router/RouteConstants';
import { GetMarketIntelligenceReportResponse } from '@/services/endpoints/marketIntelligence';
import { ReportGeneratingScreen } from './ReportGeneratingScreen';
import { SuggestedAudienceCreateOverlays } from './SuggestedAudienceCreateOverlays';
import { SuggestedAudiencesSection } from './SuggestedAudiencesSection';
import { useSuggestedAudienceCreate } from '../hooks/useSuggestedAudienceCreate';
import { MarketIntelligenceAction, MarketIntelligenceReportData, MarketIntelligenceReportFailure } from '../types';
import { Skeleton } from './MarketIntelligenceReport/Skeleton';
import { LatestReportGenerationWarning, NoGeneratedReport } from './MarketIntelligenceReport/NoGeneratedReport';
import { ReportMetaItems } from './MarketIntelligenceReport/ReportMetaItems';
import { Competitors } from './MarketIntelligenceReport/Competitors';
import { GapAnalysis } from './MarketIntelligenceReport/GapAnalysis';

interface MarketIntelligenceReportProps {
  report: MarketIntelligenceReportData | null;
  reportId: string | null;
  isGenerating: boolean;
  isLoadingReportHistory: boolean;
  showLatestReportWarning: boolean;
  onReportReady: (response: GetMarketIntelligenceReportResponse) => void;
  onReportError: (failure: MarketIntelligenceReportFailure) => void;
  onRegenerate: () => Promise<void>;
}

const ActionCard: FC<{ action: MarketIntelligenceAction }> = ({ action }) => {
  const history = useHistory();

  const handleClick = () => {
    history.push(action.ctaType === 'audience' ? RouteConstants.audiences : RouteConstants.campaigns);
  };

  return (
    <Card className='block border-primary-border bg-white p-4 drop-shadow-none'>
      <p className='text-[11px] font-semibold uppercase tracking-[0.08em] text-brand-700'>{action.eyebrow}</p>
      <p className='mt-4 text-sm font-medium leading-6 text-primary-600'>{action.description}</p>
      <p className='mt-4 text-lg font-semibold text-primary-900'>{action.title}</p>
      <Button intent='primary' size='md' className='mt-5' onClick={handleClick}>
        {action.ctaType === 'audience' ? <RuleAudienceIcon className='size-4' /> : <CampaignIcon className='size-4' />}
        <span>{action.ctaLabel}</span>
      </Button>
    </Card>
  );
};

export const MarketIntelligenceReport: FC<MarketIntelligenceReportProps> = ({
  report,
  reportId,
  isGenerating,
  isLoadingReportHistory,
  showLatestReportWarning,
  onReportReady,
  onReportError,
  onRegenerate,
}) => {
  const { t } = useTranslation('marketIntelligence');
  const { phase, activeAudience, missingDatasourceName, isEnablingConsent, createFromSuggestion, cancelGeneration, closeModals, enableConsent } =
    useSuggestedAudienceCreate();

  if (isGenerating) {
    return <ReportGeneratingScreen reportId={reportId} onComplete={onReportReady} onError={onReportError} />;
  }

  if (isLoadingReportHistory) {
    return <Skeleton />;
  }

  if (!report) {
    return <NoGeneratedReport isLatestGenerationWarning={showLatestReportWarning} />;
  }

  return (
    <div className='w-full space-y-5'>
      {showLatestReportWarning && <LatestReportGenerationWarning />}
      <ReportMetaItems report={report} onRegenerate={onRegenerate} isGenerating={isGenerating} />

      <div className='grid w-full grid-cols-2 gap-5'>
        <Competitors report={report} />
        <GapAnalysis report={report} />
      </div>

      <Card className='block border-primary-border bg-primary-background p-5 drop-shadow-none'>
        <div>
          <h3 className='text-3xl font-bold text-primary-900'>
            <span className='text-brand-700'>{t('report.strategicAction.titlePrefix')}</span> {t('report.strategicAction.titleSuffix')}
          </h3>
          <p className='text-grey-600 mt-1 text-sm'>{t('report.strategicAction.description')}</p>
        </div>

        <div className='mt-5 grid gap-4 lg:grid-cols-2'>
          {report.actions.map((action) => (
            <ActionCard key={action.id} action={action} />
          ))}
        </div>
      </Card>

      <SuggestedAudiencesSection suggestedAudiences={report.suggestedAudiences} onCreateAudience={createFromSuggestion} isCreateDisabled={phase !== 'idle'} />

      <SuggestedAudienceCreateOverlays
        phase={phase}
        activeAudience={activeAudience}
        missingDatasourceName={missingDatasourceName}
        isEnablingConsent={isEnablingConsent}
        onCancelGeneration={cancelGeneration}
        onCloseModals={closeModals}
        onEnableConsent={enableConsent}
        onRegenerate={onRegenerate}
      />
    </div>
  );
};
